import React, { createContext, Dispatch, useReducer, useContext, useCallback, useMemo } from 'react';
import { Snackbar } from '@material-ui/core';
import MuiAlert, { Color } from '@material-ui/lab/Alert';
import { AxiosError } from 'axios';

interface IProps {
  children: JSX.Element;
}

export enum SnackbarType {
  Success = 'success',
  Warning = 'warning',
  Info = 'info',
  Error = 'error',
  Close = 'close',
}

interface ISnackbarConfig {
  open: boolean;
  variant: SnackbarType;
  message: string;
}

interface ISnackbarAction {
  type: SnackbarType;
  message?: string;
}

const initialState: ISnackbarConfig = {
  open: false,
  variant: SnackbarType.Success,
  message: '',
};

const reducer = (state: ISnackbarConfig, action: ISnackbarAction) => {
  const { type, message: msg } = action;
  const message = msg || state.message;

  if (type === SnackbarType.Close) {
    return { ...state, ...{ open: false, variant: state.variant, message } };
  }

  return { ...state, ...{ open: true, variant: type, message } };
};

export const SnackbarContextComponent = (props: IProps): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { variant, open, message } = state;

  const onClose = () => dispatch({ type: SnackbarType.Close });

  return (
    <SnackbarContext.Provider value={dispatch}>
      {props.children}
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={open}
        // TransitionComponent={() => <Slide direction="left" />}
        autoHideDuration={6000}
        onClose={onClose}
      >
        <MuiAlert elevation={6} variant="filled" onClose={onClose} severity={variant.valueOf() as Color}>
          {message}
        </MuiAlert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

const SnackbarContext = createContext<Dispatch<ISnackbarAction>>(() => undefined);

const extractErrorMessage = (e: any) => {
  if (e.response && e.response.data) {
    if (typeof e.response.data === 'string') {
      return e.response.data;
    } else if (e.response.data.message) {
      return e.response.data.message;
    }
  }

  // api reponse가 아닌 다른 에러가 났을 때는 console로 확인
  console.log(e);
  return e.message || 'An error has occurred.';
};

const useSnackbar = () => {
  const snackbarContext = useContext(SnackbarContext);

  const success = useCallback(
    (message: string) => {
      snackbarContext({
        type: SnackbarType.Success,
        message,
      });
    },
    [snackbarContext],
  );

  const warning = useCallback(
    (message: string) => {
      snackbarContext({
        type: SnackbarType.Warning,
        message,
      });
    },
    [snackbarContext],
  );

  const info = useCallback(
    (message: string) => {
      snackbarContext({
        type: SnackbarType.Info,
        message,
      });
    },
    [snackbarContext],
  );

  const error = useCallback(
    (err: any) => {
      const errMsg = typeof err === 'string' ? err : extractErrorMessage(err);

      snackbarContext({
        type: SnackbarType.Error,
        message: errMsg,
      });
    },
    [snackbarContext],
  );

  return useMemo(() => ({ success, warning, info, error }), [success, warning, info, error]);
};

export default useSnackbar;
